/**
 * EventForm Component
 *
 * Form for creating and editing events.
 * Handles validation, submission, and error display.
 * Supports event type templates for auto-populating roles.
 */

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import {
  createEvent,
  updateEvent,
  getEvent,
  type Event,
} from '../../api/events';
import { getEventTypes, type EventType } from '../../api/eventTypes';
import { getErrorMessage } from '../../api/client';
import { useInvalidateEvent } from '../../hooks/queries/useEvent';
import {
  combineEventDateWithTime,
  localDayStartIso,
  toDateInputValue,
  zonedTimeInputValue,
} from '../../utils/dateFormatting';
import FormField from '../ui/FormField';
import Modal from '../ui/Modal';
import Button from '../ui/Button';

// =============================================================================
// Validation Schema
// =============================================================================

const eventSchema = z.object({
  name: z.string().min(1, 'Event name is required').max(200, 'Name too long'),
  venue: z.string().min(1, 'Venue is required').max(200, 'Venue too long'),
  network: z.string().max(100, 'Network too long').optional().nullable(),
  sport: z.string().max(100).optional().nullable(),
  eventType: z.string().max(100).optional().nullable(),
  eventDate: z.string().min(1, 'Event date is required'),
  callTime: z.string().optional().nullable(),
  startTime: z.string().optional().nullable(),
  endTime: z.string().optional().nullable(),
  tocInfo: z.string().optional().nullable(),
  engineeringNotes: z.string().optional().nullable(),
  additionalDetails: z.string().optional().nullable(),
  status: z.enum(['DRAFT', 'PUBLISHED', 'COMPLETED', 'CANCELLED']).default('DRAFT'),
});

type EventFormData = z.infer<typeof eventSchema>;

// =============================================================================
// EventForm Component
// =============================================================================

interface EventFormProps {
  eventId?: string;
  onSuccess?: (event: Event) => void;
}

export default function EventForm({ eventId, onSuccess }: EventFormProps) {
  const navigate = useNavigate();
  const invalidateEvent = useInvalidateEvent();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(!!eventId);

  // Event types state
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [eventTypesError, setEventTypesError] = useState<string | null>(null);
  const [selectedEventTypeId, setSelectedEventTypeId] = useState<string | null>(null);
  const [eventTypesLoading, setEventTypesLoading] = useState(true);

  // Confirmation dialog state for event type change during edit
  const [showTypeChangeWarning, setShowTypeChangeWarning] = useState(false);
  const [pendingEventTypeId, setPendingEventTypeId] = useState<string | null>(null);

  // Track existing event data for the warning
  const [existingRoleCount, setExistingRoleCount] = useState(0);
  const [existingAssignmentCount, setExistingAssignmentCount] = useState(0);
  // Track the original event type to detect actual changes
  const [originalEventType, setOriginalEventType] = useState<string | null>(null);

  const isEditing = !!eventId;

  // Get selected event type for preview
  const selectedEventType = eventTypes.find((et) => et.id === selectedEventTypeId);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EventFormData>({
    resolver: zodResolver(eventSchema),
    defaultValues: {
      status: 'DRAFT',
    },
  });

  // Extract HH:MM from ISO datetime string, as wall-clock time in the org's zone
  const extractTime = (isoString: string | null | undefined): string | null => {
    if (!isoString) return null;
    return zonedTimeInputValue(isoString);
  };

  // Load event types on mount (for both create and edit)
  useEffect(() => {
    const loadEventTypes = async () => {
      try {
        const response = await getEventTypes();
        setEventTypes(response.eventTypes);
        setEventTypesError(null);
      } catch (err) {
        setEventTypesError(
          err instanceof Error ? err.message : 'Failed to load event templates'
        );
      } finally {
        setEventTypesLoading(false);
      }
    };

    loadEventTypes();
  }, []);

  // Load existing event data if editing
  useEffect(() => {
    if (!eventId) return;

    const loadEvent = async () => {
      try {
        const { event } = await getEvent(eventId);
        reset({
          name: event.name,
          venue: event.venue,
          network: event.network,
          sport: event.sport,
          eventType: event.eventType,
          // The org-zone calendar day; the UTC day is the day before for any
          // org east of UTC (midnight CEST is 22:00Z the previous day).
          eventDate: toDateInputValue(event.eventDate),
          callTime: extractTime(event.callTime),
          startTime: extractTime(event.startTime),
          endTime: extractTime(event.endTime),
          tocInfo: event.tocInfo,
          engineeringNotes: event.engineeringNotes,
          additionalDetails: event.additionalDetails,
          status: event.status,
        });

        // Track existing roles/assignments for the warning
        const roleCount = event.roles?.length || 0;
        const assignmentCount = event.roles?.reduce(
          (sum: number, r: { assignments?: unknown[] }) => sum + (r.assignments?.length || 0),
          0
        ) || 0;
        setExistingRoleCount(roleCount);
        setExistingAssignmentCount(assignmentCount);
        setOriginalEventType(event.eventType || null);
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setIsLoading(false);
      }
    };

    loadEvent();
  }, [eventId, reset]);

  // Handle event type dropdown change when editing
  const handleEventTypeChange = (newEventTypeId: string | null) => {
    if (!isEditing) {
      setSelectedEventTypeId(newEventTypeId);
      return;
    }

    // If clearing the selection back to the original state, no warning needed
    if (!newEventTypeId) {
      setSelectedEventTypeId(null);
      return;
    }

    // Check if the event has existing roles or assignments
    if (existingRoleCount > 0 || existingAssignmentCount > 0) {
      setPendingEventTypeId(newEventTypeId);
      setShowTypeChangeWarning(true);
    } else {
      setSelectedEventTypeId(newEventTypeId);
    }
  };

  const confirmTypeChange = () => {
    setSelectedEventTypeId(pendingEventTypeId);
    setShowTypeChangeWarning(false);
    setPendingEventTypeId(null);
  };

  const cancelTypeChange = () => {
    setShowTypeChangeWarning(false);
    setPendingEventTypeId(null);
  };

  const onSubmit = async (data: EventFormData) => {
    setError(null);
    setIsSubmitting(true);

    try {
      // Store the YYYY-MM-DD picker value as midnight in the ORG's zone — not
      // UTC midnight (`new Date('2026-05-15')`, the previous day anywhere west
      // of UTC) and not the device's midnight (an admin travelling in Denver
      // would otherwise store a different instant than one at home). Every
      // reader — this app, emails, the ICS feed, billing — reads it in the
      // org's zone.
      const eventDateISO = localDayStartIso(data.eventDate);

      // Combine eventDate with time fields: wall-clock times in the org's zone
      // (shared with the bulk time-update flow).
      const formatTimeToISO = (timeStr: string | null | undefined): string | null =>
        timeStr ? combineEventDateWithTime(eventDateISO, timeStr) : null;

      // Determine event type name: from template or custom input
      const eventTypeName = selectedEventTypeId
        ? selectedEventType?.name || null
        : data.eventType || null;

      const payload = {
        ...data,
        eventDate: eventDateISO,
        network: data.network || null,
        sport: data.sport || null,
        eventType: eventTypeName,
        eventTypeId: selectedEventTypeId || null,
        callTime: formatTimeToISO(data.callTime),
        startTime: formatTimeToISO(data.startTime),
        endTime: formatTimeToISO(data.endTime),
        tocInfo: data.tocInfo || null,
        engineeringNotes: data.engineeringNotes || null,
        additionalDetails: data.additionalDetails || null,
      };

      let result: Event;

      if (isEditing) {
        const response = await updateEvent(eventId, payload);
        result = response.event;
        invalidateEvent(eventId);
      } else {
        const response = await createEvent(payload);
        result = response.event;
      }

      if (onSuccess) {
        onSuccess(result);
      } else {
        navigate(`/admin/events/${result.id}`);
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-2 gap-6">
            <div className="h-10 bg-gray-200 rounded"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
          </div>
          <div className="grid grid-cols-3 gap-6">
            <div className="h-10 bg-gray-200 rounded"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  // Determine the current dropdown value: selectedEventTypeId if user picked one,
  // otherwise try to match the original eventType name to an event type
  const dropdownValue = selectedEventTypeId || '';

  // Build the event type selector (shared between create and edit)
  const renderEventTypeSelector = () => {
    if (eventTypesLoading) {
      return (
        <div className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500">
          Loading templates...
        </div>
      );
    }

    if (eventTypesError) {
      return (
        <div className="block w-full px-3 py-2 border border-amber-300 bg-amber-50 rounded-md text-sm text-amber-800">
          Couldn't load event templates ({eventTypesError}). You can still create the event with a custom Event Type below.
        </div>
      );
    }

    if (eventTypes.length > 0) {
      return (
        <select
          id="eventTypeSelect"
          value={dropdownValue}
          onChange={(e) => handleEventTypeChange(e.target.value || null)}
          className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack bg-white"
        >
          <option value="">
            {isEditing && originalEventType && !selectedEventTypeId
              ? originalEventType
              : 'No Template (Custom)'}
          </option>
          {eventTypes.map((et) => (
            <option key={et.id} value={et.id}>
              {et.name} ({et.templateRoles.length} roles)
            </option>
          ))}
        </select>
      );
    }

    // No templates available - show text input
    return (
      <input
        id="eventType"
        type="text"
        {...register('eventType')}
        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack"
        placeholder="e.g., Regular Season, Playoffs"
      />
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        {isEditing ? 'Edit Event' : 'Create New Event'}
      </h2>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
            {error}
          </div>
        )}

        {/* Basic Info Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Event Name */}
          <FormField
            label="Event Name"
            {...register('name')}
            error={errors.name}
            required
            placeholder="e.g., Steelers vs Ravens"
          />

          {/* Venue */}
          <FormField
            label="Venue"
            {...register('venue')}
            error={errors.venue}
            required
            placeholder="e.g., Acrisure Stadium"
          />
        </div>

        {/* Network, Sport & Type */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <FormField
            label="Network / Audience"
            {...register('network')}
            error={errors.network}
            placeholder="e.g., ESPN+, SNP"
          />

          <FormField
            label="Sport"
            {...register('sport')}
            placeholder="e.g., Football, Hockey"
          />

          <div>
            <label htmlFor="eventType" className="block text-sm font-medium text-gray-700 mb-1">
              Event Type
              {/* Info bubble: event types are role templates. Hover on
                  desktop, focus/tap on touch (button is focusable). */}
              <span className="relative inline-flex group ml-1.5">
                <button
                  type="button"
                  aria-label="What are event types?"
                  className="w-4 h-4 bg-gray-200 text-gray-600 text-[10px] font-semibold leading-none inline-flex items-center justify-center hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-rack"
                >
                  i
                </button>
                <span
                  role="tooltip"
                  className="invisible opacity-0 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 transition-opacity absolute left-6 top-1/2 -translate-y-1/2 z-20 w-72 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg font-normal"
                >
                  Event Types are templates you manage on the Settings &rarr; Event
                  Types page. Give a type a set of crew roles there, and every
                  event created with that type, including CSV imports that name
                  it (capitalization doesn't matter), starts with those roles added
                  automatically.
                </span>
              </span>
            </label>
            {renderEventTypeSelector()}
          </div>
        </div>

        {/* Template Roles Preview */}
        {selectedEventType && selectedEventType.templateRoles.length > 0 && (
          <div className="bg-surface-panel border border-hairline border-l-[3px] border-l-rack p-4">
            <div className="flex items-start">
              <InfoIcon className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-800">
                  Roles from "{selectedEventType.name}" template
                </p>
                <p className="text-sm text-blue-600 mt-1">
                  {isEditing
                    ? 'The following roles will replace the current roles when you save:'
                    : 'The following roles will be automatically created:'}
                </p>
                <ul className="mt-2 space-y-1">
                  {selectedEventType.templateRoles.map((role) => (
                    <li key={role.id} className="text-sm text-blue-700 flex items-center">
                      <span className="w-1.5 h-1.5 bg-blue-500 mr-2"></span>
                      {role.roleName}
                      <span className="text-blue-500 ml-1">&times; {role.requiredCount}</span>
                      {role.description && (
                        <span className="text-blue-400 ml-2">— {role.description}</span>
                      )}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-blue-500 mt-2">
                  Total crew needed: {selectedEventType.templateRoles.reduce((sum, r) => sum + r.requiredCount, 0)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Date & Time Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Event Date */}
          <FormField
            label="Event Date"
            type="date"
            {...register('eventDate')}
            error={errors.eventDate}
            required
          />
        </div>

        {/* Time Fields */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Crew Call Time */}
          <FormField
            label="Crew Call Time"
            type="time"
            {...register('callTime')}
            helperText="When crew should arrive"
          />

          {/* Start Time */}
          <FormField
            label="Start Time"
            type="time"
            {...register('startTime')}
            helperText="When the event starts"
          />

          {/* End Time */}
          <FormField
            label="End Time"
            type="time"
            {...register('endTime')}
            helperText="Expected end time"
          />
        </div>

        {/* Status */}
        <div>
          <label htmlFor="status" className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
            Status
            {/* Info bubble: what each status means for crew visibility.
                Hover on desktop, focus/tap on touch (button is focusable). */}
            <span className="relative inline-flex group">
              <button
                type="button"
                aria-label="What do the statuses mean?"
                className="w-4 h-4 bg-gray-200 text-gray-600 text-[10px] font-semibold leading-none inline-flex items-center justify-center hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-rack"
              >
                i
              </button>
              <span
                role="tooltip"
                className="invisible opacity-0 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 transition-opacity absolute left-6 top-1/2 -translate-y-1/2 z-20 w-72 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg font-normal"
              >
                <span className="block mb-1.5"><span className="font-semibold">Draft</span> : hidden from crew while you plan. Assign roles and edit freely; nobody is notified.</span>
                <span className="block mb-1.5"><span className="font-semibold">Published</span> : visible to crew. They can set availability, and assigned crew can see and confirm their roles.</span>
                <span className="block mb-1.5"><span className="font-semibold">Completed</span> : the event has happened. Kept for reports, hours, and payroll.</span>
                <span className="block"><span className="font-semibold">Cancelled</span> : the event is off. It leaves crew dashboards and pending-notification lists.</span>
              </span>
            </span>
          </label>
          <select
            id="status"
            {...register('status')}
            className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack bg-white"
          >
            <option value="DRAFT">Draft</option>
            <option value="PUBLISHED">Published</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>

        {/* Additional Info Section */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-sm font-medium text-gray-700 mb-4">Additional Information</h3>

          {/* TOC Info */}
          <div className="mb-4">
            <label htmlFor="tocInfo" className="block text-sm font-medium text-gray-700 mb-1">
              TOC Information
            </label>
            <textarea
              id="tocInfo"
              rows={2}
              {...register('tocInfo')}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack"
              placeholder="TOC-specific information..."
            />
          </div>

          {/* Engineering Notes */}
          <div className="mb-4">
            <label htmlFor="engineeringNotes" className="block text-sm font-medium text-gray-700 mb-1">
              Engineering Notes
            </label>
            <textarea
              id="engineeringNotes"
              rows={2}
              {...register('engineeringNotes')}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack"
              placeholder="Technical notes for engineering..."
            />
          </div>

          {/* Additional Details */}
          <div>
            <label htmlFor="additionalDetails" className="block text-sm font-medium text-gray-700 mb-1">
              Additional Details
            </label>
            <textarea
              id="additionalDetails"
              rows={3}
              {...register('additionalDetails')}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack"
              placeholder="Any other relevant information..."
            />
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/admin/events')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={isSubmitting}
            loadingText={isEditing ? 'Saving...' : 'Creating...'}
          >
            {isEditing ? 'Save Changes' : 'Create Event'}
          </Button>
        </div>
      </form>

      {/* Event Type Change Warning Modal */}
      <Modal
        isOpen={showTypeChangeWarning}
        onClose={cancelTypeChange}
        size="md"
        labelledBy="event-type-change-title"
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 id="event-type-change-title" className="text-lg font-semibold text-gray-900">
            Change Event Type?
          </h3>
        </div>
        <div className="px-6 py-4">
          <div className="flex items-start">
            <WarningIcon className="w-5 h-5 text-amber-500 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <p className="text-sm text-gray-700">
                This event currently has <strong>{existingRoleCount} role{existingRoleCount !== 1 ? 's' : ''}</strong>
                {existingAssignmentCount > 0 && (
                  <> and <strong>{existingAssignmentCount} crew assignment{existingAssignmentCount !== 1 ? 's' : ''}</strong></>
                )}.
              </p>
              <p className="text-sm text-red-600 font-medium mt-2">
                Changing the event type will delete all existing roles
                {existingAssignmentCount > 0 ? ' and assignments' : ''} and
                replace them with the roles from the new template.
              </p>
              <p className="text-sm text-gray-500 mt-2">
                This action cannot be undone.
              </p>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 rounded-b-lg">
          <button
            type="button"
            onClick={cancelTypeChange}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirmTypeChange}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
          >
            Replace Roles
          </button>
        </div>
      </Modal>
    </div>
  );
}

// =============================================================================
// Icons
// =============================================================================

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
      />
    </svg>
  );
}
